import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react"
import { useMutation } from "react-relay"
import { HandleCallMutation } from "./CallingQuery"
import WebRTCService from "./WebRTCService"

const CallContext = createContext(null)

export function CallProvider({ children }) {
  const [incomingCalls, setIncomingCalls] = useState([])
  const [activeCall, setActiveCall] = useState(null)
  const [isOutgoingCalling, setIsOutgoingCalling] = useState(false)
  const [acceptingCall, setAcceptingCall] = useState(null)
  const [rejectingCall, setRejectingCall] = useState(null)
  const [isTerminating, setIsTerminating] = useState(false)
  const webrtcServiceRef = useRef(null)
  const ringtoneRef = useRef(null)
  const outgoingRingtoneRef = useRef(null)

  const [commitHandleCall] = useMutation(HandleCallMutation)

  // Play incoming ringtone in loop
  const playRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/incoming-ringtone.wav`)
        ringtoneRef.current.loop = true
      }
      ringtoneRef.current.play().catch((error) => {
        console.error("Error playing ringtone:", error)
      })
    } catch (error) {
      console.error("Error initializing ringtone:", error)
    }
  }, [])

  // Stop incoming ringtone
  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause()
      ringtoneRef.current.currentTime = 0
    }
  }, [])

  // Play outgoing ringtone in loop
  const playOutgoingRingtone = useCallback(() => {
    try {
      if (!outgoingRingtoneRef.current) {
        outgoingRingtoneRef.current = new Audio(`${process.env.PUBLIC_URL}/audio/outgoing-ringtone.wav`)
        outgoingRingtoneRef.current.loop = true
      }
      outgoingRingtoneRef.current.play().catch((error) => {
        console.error("Error playing outgoing ringtone:", error)
      })
    } catch (error) {
      console.error("Error initializing outgoing ringtone:", error)
    }
  }, [])

  // Stop outgoing ringtone
  const stopOutgoingRingtone = useCallback(() => {
    if (outgoingRingtoneRef.current) {
      outgoingRingtoneRef.current.pause()
      outgoingRingtoneRef.current.currentTime = 0
    }
  }, [])

  // Handle incoming ringtone based on call status
  useEffect(() => {
    if (activeCall?.callType === "INCOMING" && activeCall?.callStatus === "RINGING") {
      playRingtone()
    } else {
      stopRingtone()
    }

    return () => {
      stopRingtone()
    }
  }, [activeCall?.callType, activeCall?.callStatus, playRingtone, stopRingtone])

  // Handle outgoing ringtone based on call status
  useEffect(() => {
    if (
      activeCall?.callType === "OUTGOING" && 
      (activeCall?.callStatus === "INPROGRESS" || 
       activeCall?.callStatus === "INITIATING")
    ) {
      playOutgoingRingtone()
    } else if (
      activeCall?.callType === "OUTGOING" && 
      (activeCall?.callStatus === "ACCEPTED" || 
        activeCall?.callStatus === "TERMINATED")) {
      stopOutgoingRingtone()
    }

    return () => {
      stopOutgoingRingtone()
    }
  }, [activeCall?.callType, activeCall?.callStatus, playOutgoingRingtone, stopOutgoingRingtone])

  const isActiveCall = (callId) => {
    return activeCall?.callId === callId
  }

  const addIncomingCall = useCallback((call) => {
    console.log("addIncomingCall: ", {call})
    setIncomingCalls((prevCalls) => [...prevCalls, {...call, callType: "INCOMING"}])
  }, [])

  const updateIncomingCall = useCallback((call) => {
    console.log("updateIncomingCall: ", {call})
    setIncomingCalls((prevCalls) =>
      prevCalls.map((c) => (c.id === call.id ? { ...c, ...call } : c))
    )
  }, [])

  const removeIncomingCall = (callId) => {
    console.log("removeIncomingCall: ", callId)
    setIncomingCalls((prevCalls) => prevCalls.filter((c) => c.callId !== callId))
  }

  const rejectIncomingCall = async (call) => {
    if (!call && activeCall && activeCall?.callStatus === "ACCEPTED") {
      console.log("There is an active call")
      return
    }
    console.log("rejectIncomingCall: ", {call})
    setRejectingCall(call)
    try {
      const {callId, etag } = call
      if (!callId || !etag) throw new Error("Invalid call")
      commitHandleCall({
        variables: {
          input: {
            callId: call.callId,
            action: "REJECT",
            clientMutationId: call.etag,
          }
        },
        onCompleted: (res) => {
          if (res?.handleCall?.error) {
            console.error(res.handleCall.error)
            return
          }

          console.log("CALL REJECTED: ", {call})
          removeIncomingCall(callId)
          setRejectingCall(null)
        },
        onError: (error) => {
          console.error("rejectIncomingCall: ",error)
          setRejectingCall(null)
        }
      })
    } catch (error) {
      setRejectingCall(null)
      console.error("rejectIncomingCall: ", error)
    }
  }

  const acceptIncomingCall = async (call) => {
    if (!call && activeCall?.callStatus === "ACCEPTED") {
      console.log("There is an active call")
      return
    }
    console.log("acceptIncomingCall", {call})
    setAcceptingCall(call)

    try {
      const { callId, callInfo, etag } = call
      if (!callId || !callInfo || !etag) throw new Error("Invalid call")
      const sdpOffer = callInfo?.session?.sdp
      if (!sdpOffer) throw new Error("No offer sdp found in call data")

      // Check and request microphone permission before initializing WebRTC
      const permissionStatus = await WebRTCService.checkMicrophonePermission()
      
      if (permissionStatus.state === "denied") {
        alert("Microphone access is blocked. Please enable it in your browser settings to accept calls.")
        setAcceptingCall(null)
        return
      }

      if (permissionStatus.state === "prompt") {
        const permissionResult = await WebRTCService.requestMicrophonePermission()
        
        if (!permissionResult.granted) {
          alert(permissionResult.error || "Microphone permission is required to accept calls.")
          setAcceptingCall(null)
          return
        }
      }

      // initialize WebRTC service
      webrtcServiceRef.current = new WebRTCService()
      await webrtcServiceRef.current.initialize()

      // create sdp answer 
      const sdpAnswer = await webrtcServiceRef.current.createAnswer(sdpOffer)

      commitHandleCall({
        variables: {
          input: {
            callId: callId,
            action: "RECEIVE",
            session: {
              sdp_type: "answer",
              sdp: sdpAnswer,
            },
            clientMutationId: etag,
          }
        },
        onCompleted: (res) => {
          if (res?.handleCall?.error) {
            console.log(res.handleCall.error)
            return
          }

          console.log("CALL ACCEPTED: ", {call})
          setActiveCall({
            ...call,
            callType: "INCOMING",
            callStatus: "ACCEPTED"
          })
          removeIncomingCall(callId)
          setAcceptingCall(null)
        },
        onError: (error) => {
          setAcceptingCall(null)
          console.error("acceptIncomingCall: ", error)
        }
      })
    } catch (error) {
      setAcceptingCall(null)
      console.error("acceptIncomingCall: ", error)
      
      // Show user-friendly error message
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        alert("Microphone permission denied. Please allow microphone access to accept calls.")
      } else if (error.name === "NotFoundError") {
        alert("No microphone found. Please connect a microphone to accept calls.")
      }
    }
  }

  const terminateActiveCall = () => {
    console.log("terminateActiveCall: ", activeCall)
    if (!activeCall || isTerminating) return
    setIsTerminating(true)
    
    try {
      commitHandleCall({
        variables: {
          input: {
            callId: activeCall.callId,
            action: "TERMINATE",
            clientMutationId: activeCall.etag,
          }
        },
        onCompleted: (res) => {
          if (res?.handleCall?.error) {
            console.error(res.handleCall.error)
            setIsTerminating(false)
            return
          }

          console.log("CALL TERMINATED: ", {res})
          
          // Clean up WebRTC resources
          if (webrtcServiceRef.current) {
            webrtcServiceRef.current.cleanup()
            webrtcServiceRef.current = null
          }
          setIsOutgoingCalling(false)
          setActiveCall(null)
          setIsTerminating(false)
        },
        onError: (error) => {
          setIsTerminating(false)
          setIsOutgoingCalling(false)
          console.error("terminateActiveCall: ", error)
        }
      })
    } catch (error) {
      setIsTerminating(false)
      setIsOutgoingCalling(false)
      console.error("terminateActiveCall: ", error)
    }
  }

  const initiateOutgoingCall = async ({
    channelId,
    externalId,
  }) => {
    if (activeCall?.callStatus === "ACCEPTED") {
      console.log("There is an active call")
      return
    }
    console.log("initiateOutgoingCall: ", {channelId, externalId})
    
    try {
      // Check and request microphone permission before proceeding
      const permissionStatus = await WebRTCService.checkMicrophonePermission()
      
      if (permissionStatus.state === "denied") {
        alert("Microphone access is blocked. Please enable it in your browser settings to make calls.")
        return
      }

      if (permissionStatus.state === "prompt") {
        const permissionResult = await WebRTCService.requestMicrophonePermission()
        
        if (!permissionResult.granted) {
          alert(permissionResult.error || "Microphone permission is required to make calls.")
          return
        }
      }

      setIsOutgoingCalling(true)
      setActiveCall({
        channelId,
        externalId,
        to: externalId,
        callType: "OUTGOING",
        callStatus: "INPROGRESS"
      })

      // initialize WebRTC service
      webrtcServiceRef.current = new WebRTCService()
      await webrtcServiceRef.current.initialize()

      // createOffer() already sets local description internally
      const sdpOffer = await webrtcServiceRef.current.createOffer()
      
      commitHandleCall({
        variables: {
          input: {
            action: "INITIATE",
            wChannelId: channelId,
            to: externalId,
            session: {
              sdp_type: "offer",
              sdp: sdpOffer,
            },
          }
        },
        onCompleted: (res) => {
          const call = res?.handleCall?.call
          const callId = call?.callId
          console.log("CALL INITIATED: ", {res})
          if (!callId) {
            alert("Failed to initiate business initiated call!")
            setIsOutgoingCalling(false)
            setActiveCall(null)
            return
          }
        
          // set active call
          setActiveCall({
            callId,
            channelId,
            externalId,
            to: call?.to,
            session: {
              sdpType: "offer",
              sdp: sdpOffer,
            },
            callType: "OUTGOING",
            callStatus: "INITIATING"
          })
          setIsOutgoingCalling(false)
        },
        onError: (error) => {
          setIsOutgoingCalling(false)
          console.error("initiateOutgoingCall: ",error)
        }
      })
    } catch(error) {
      setIsOutgoingCalling(false)
      setActiveCall(null)
      console.error("initiateOutgoingCall: ",error)
      
      // Show user-friendly error message
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        alert("Microphone permission denied. Please allow microphone access to make calls.")
      } else if (error.name === "NotFoundError") {
        alert("No microphone found. Please connect a microphone to make calls.")
      }
    }
  }

  const startOutgoingCall = async (call) => {
    if (activeCall?.callStatus === "ACCEPTED") {
      console.log("There is an outgoing accepted call")
      return
    }
    console.log("startOutgoingCall: ", {call})
    removeIncomingCall(call.callId)
    setActiveCall((prev) => ({ ...prev, ...call}))
    
    try {
      console.log("webrtcServiceRef: ", webrtcServiceRef)
      if (!webrtcServiceRef.current) {
        throw new Error("WebRTC service not initialized")
      }

      // verify local stream is still active
      if (!webrtcServiceRef.current.localStream) {
        console.error("Local stream not found, reinitializing...")
        throw new Error("Local stream not available")
      }

      const localTracks = webrtcServiceRef.current.localStream.getTracks()
      if (localTracks.length === 0 || !localTracks.some(track => track.readyState === 'live')) {
        console.error("No active local tracks found")
        throw new Error("Local audio tracks not active")
      }

      console.log("Local stream active with tracks:", localTracks.map(t => ({ kind: t.kind, readyState: t.readyState })))

      const remoteAnswer = new RTCSessionDescription({
        type: 'answer',
        sdp: call.session.sdp
      })

      console.log("remoteAnswer: ", remoteAnswer)
      
      await webrtcServiceRef.current.peerConnection.setRemoteDescription(remoteAnswer)
      
      console.log("Remote description set successfully")
      console.log("Peer connection state:", webrtcServiceRef.current.peerConnection.connectionState)
      console.log("ICE connection state:", webrtcServiceRef.current.peerConnection.iceConnectionState)
      console.log("Signaling state:", webrtcServiceRef.current.peerConnection.signalingState)
      
      // check for remote tracks
      const receivers = webrtcServiceRef.current.peerConnection.getReceivers()
      console.log("Remote receivers count:", receivers.length)
      receivers.forEach((receiver, index) => {
        console.log(`Receiver ${index}:`, {
          track: receiver.track,
          trackEnabled: receiver.track?.enabled,
          trackReadyState: receiver.track?.readyState,
          trackKind: receiver.track?.kind
        })
      })
      
      // Manually trigger remote track check after a short delay
      setTimeout(() => {
        console.log("Manual check after setRemoteDescription")
        webrtcServiceRef.current.checkAndPlayRemoteTracks()
      }, 500)
      
      setActiveCall((prev) => ({ 
        ...prev, 
        callStatus: "ACCEPTED",
      }))
      
    } catch (error) {
      console.error("Failed to start outgoing call: ", error)
      setActiveCall(null)
    }
  }

  const makeOutgoinCallRinging = async (call) => {
    console.log("makeOutgoinCallRinging: ", {call})
    if (activeCall?.callId !== call.callId) return
    removeIncomingCall(call.callId)
    setActiveCall((prev) => ({ ...prev, ...call}))
  }

  const makeIncomingCallActive = (call) => {
    if (!activeCall && incomingCalls.length === 0) {
      addIncomingCall(call)
      setActiveCall((prev) => (!prev && incomingCalls.length === 0 ? call : prev))
    } else {
      addIncomingCall(call)
    }
  }

  const makeCallAccepted = async (call) => {
    console.log("makeCallAccepted: ", {call})
    removeIncomingCall(call.callId)
    // setActiveCall(call)
  }

  const makeCallTerminated = useCallback((call) => {
    console.log("makeCallTerminated: ", {call})
    
    setActiveCall((currentActiveCall) => {
      console.log("activeCall: ", {currentActiveCall})
      console.log("same: ", currentActiveCall?.callId, call.callId)
      
      if (currentActiveCall?.callId === call.callId) {
        console.log("setting active call to null")
        if (webrtcServiceRef.current) {
          webrtcServiceRef.current.cleanup()
          webrtcServiceRef.current = null
        }
        
        return null
      }
      return currentActiveCall
    })

    removeIncomingCall(call.callId)
  }, [])

  const handleCallActive = useCallback((call) => {
    console.log("handleCallActive: ", call)
    removeIncomingCall(call.callId)
    setActiveCall((prevCall) => {
      console.log("prevCall: ", prevCall)
      if (prevCall.callId === call.callId && prevCall?.callStatus === "RINGING") {
        return null
      } else {
        return prevCall
      }
    })
  }, [])

  // handle postMessage from child window to initiate voice call
  useEffect(() => {
    const handlePostMessage = async (event) => {
      console.log("handlePostMessage: ", event.data)
      if (event.data?.type === "startVoiceCall") {
        const { channelId, externalId } = event.data.data || {}
        
        try {
          if (!channelId || !externalId) throw new Error("Missing channelId and externalId")
          await initiateOutgoingCall({
            channelId,
            externalId,
          })
        } catch (error) {
          console.error("Failed to initiate call:", error)
        }
      }
    }

    window.addEventListener("message", handlePostMessage)

    return () => {
      window.removeEventListener("message", handlePostMessage)
    }
  }, [])

  const value = {
    incomingCalls,
    addIncomingCall,
    updateIncomingCall,
    removeIncomingCall,
    rejectIncomingCall,
    acceptIncomingCall,
    activeCall,
    setActiveCall,
    isActiveCall,
    acceptingCall,
    rejectingCall,
    isTerminating,
    setIsTerminating,
    isOutgoingCalling,
    terminateActiveCall,
    initiateOutgoingCall,
    startOutgoingCall,
    makeCallAccepted,
    makeCallTerminated,
    makeIncomingCallActive,
    makeOutgoinCallRinging,
    handleCallActive
  }

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>
}

export function useCallContext() {
  const context = useContext(CallContext)
  if (!context) {
    throw new Error("useCallContext must be used within a CallProvider")
  }
  return context
}
